import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  PlusOutlined,
  HolderOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Button,
  Input,
  Select,
  Switch,
  InputNumber,
  DatePicker,
  message,
  Tooltip,
} from 'antd';
import { Draggable } from 'react-beautiful-dnd';
import StrictModeDroppable from './StrictModeDroppable';
import dayjs from 'dayjs';
import { createOptionEntry, updateOptionTitle } from '../../utils/optionUtils';
import CustomDelete from '../../../../../../../assets/customSVG/CustomDelete';

const { TextArea } = Input;
const { Option, OptGroup } = Select;

/**
 * Renders a single component editor card within a screen or form.
 */
export default function FlowComponentEditor({
  component,
  componentIndex,
  screenId,
  parentFormIndex = null,
  dragHandleProps,
  onUpdate,
  onDelete,
  isFormChild = false,
  formUtils = {},
  optInUtils = {},
  brandId,
  flowId,
  crmAttributes = [],
  crmFieldMappings = {},
  onCrmFieldChange,
  crmAttributesLoading = false,
  duplicateNames = new Set(),
  screenIndex = null,
}) {
  if (!component) return null;

  const {
    handleUpdateFormComponent,
    handleDeleteFormComponent,
    createFormDroppableId,
    createFormChildDraggableId,
  } = formUtils;

  const updateComponent = (updates) => {
    if (typeof onUpdate === 'function') {
      onUpdate(updates);
    }
  };

  const deleteComponent = () => {
    if (typeof onDelete === 'function') {
      onDelete();
    }
  };

  const handleChange = (field, value) => {
    updateComponent({ [field]: value });
  };

  const isFooter = component.type === 'Footer';
  const isForm = component.type === 'Form';
  const componentOptions = Array.isArray(component['data-source'])
    ? component['data-source']
    : [];
  const imageFileInputRef = useRef(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const interactiveComponentTypes = useMemo(
    () => [
      'TextInput',
      'TextArea',
      'DatePicker',
      'CheckboxGroup',
      'RadioButtonsGroup',
      'Dropdown',
      'OptIn',
    ],
    [],
  );
  const isInteractiveField = interactiveComponentTypes.includes(component.type);
  const allowCrmSync =
    isInteractiveField &&
    !['CheckboxGroup'].includes(component.type);
  const hasRequiredToggle =
    isInteractiveField &&
    Object.prototype.hasOwnProperty.call(component, 'required');
  const isPhoneInputType =
    component.type === 'TextInput' && component['input-type'] === 'phone';
  const isEmailInputType =
    component.type === 'TextInput' && component['input-type'] === 'email';

  const currentCrmField = useMemo(() => {
    const prefixedKey =
      screenIndex !== null
        ? `${screenIndex}_${component.name}`
        : component.name;
    return (
      crmFieldMappings?.[prefixedKey] ||
      crmFieldMappings?.[component.name] ||
      ''
    );
  }, [crmFieldMappings, component.name, screenIndex]);

  const isDuplicateName =
    isInteractiveField &&
    duplicateNames &&
    typeof duplicateNames.has === 'function' &&
    duplicateNames.has(component.name);
  const duplicateWarning = isDuplicateName ? (
    <p className="mt-1 text-xs text-red-500">
      Field name must be unique within this screen.
    </p>
  ) : null;

  const usedCrmFields = useMemo(() => {
    const used = new Set();
    const currentPrefixedKey =
      screenIndex !== null
        ? `${screenIndex}_${component.name}`
        : component.name;

    Object.entries(crmFieldMappings || {}).forEach(([fieldId, crmField]) => {
      if (
        !crmField ||
        fieldId === component.name ||
        fieldId === currentPrefixedKey
      )
        return;
      used.add(crmField);
    });
    return used;
  }, [crmFieldMappings, component.name, screenIndex]);

  const normalizeCrmAttributeType = (type) => {
    if (!type) return '';
    const normalized = String(type).toLowerCase();

    if (['phone_number', 'phone_numbers', 'phone'].includes(normalized)) {
      return 'number';
    }

    if (['email', 'password', 'text'].includes(normalized)) {
      return 'string';
    }

    if (normalized === 'datetime') {
      return 'date';
    }

    if (['date', 'boolean', 'number', 'string'].includes(normalized)) {
      return normalized;
    }

    return normalized;
  };

  const expectedCrmAttributeTypes = useMemo(() => {
    if (!allowCrmSync) return null;

    switch (component.type) {
      case 'DatePicker':
        return ['date'];
      case 'OptIn':
        return ['boolean'];
      case 'TextInput':
        if (isPhoneInputType) {
          return ['phone_number', 'phone_numbers', 'phone'];
        }
        if (isEmailInputType) {
          return ['email'];
        }
        return ['string'];
      case 'TextArea':
      case 'Dropdown':
      case 'RadioButtonsGroup':
      case 'CheckboxGroup':
        return ['string'];
      default:
        return null;
    }
  }, [
    allowCrmSync,
    component.type,
    isEmailInputType,
    isPhoneInputType,
  ]);

  const normalizedExpectedCrmAttributeTypes = useMemo(
    () =>
      (expectedCrmAttributeTypes || [])
        .map((type) => normalizeCrmAttributeType(type))
        .filter(Boolean),
    [expectedCrmAttributeTypes],
  );

  /**
   * Filters and sorts available CRM attributes for the dropdown
   * - Excludes age-related fields (only date_of_birth should be used)
   * - Filters out already mapped fields (except current field)
   * - Sorts by scope and then alphabetically by label
   */
  const availableCrmAttributes = useMemo(
    () =>
      (Array.isArray(crmAttributes) ? crmAttributes : [])
        .filter(
          (attr) =>
            attr?.name &&
            // Exclude age and age_range fields - only date_of_birth should be used
            !['age', 'age_range'].includes(attr.name) &&
            (attr.name === currentCrmField || !usedCrmFields.has(attr.name)),
        )
        .filter((attr) => {
          if (!normalizedExpectedCrmAttributeTypes.length) return true;
          const attrType = normalizeCrmAttributeType(
            attr.type || attr.attribute_type,
          );
          if (!attrType) return false;
          return normalizedExpectedCrmAttributeTypes.includes(attrType);
        })
        .sort((a, b) => {
          if (a.scopeId !== b.scopeId) {
            return (a.scopeId || 0) - (b.scopeId || 0);
          }
          return (a.label || a.name).localeCompare(b.label || b.name);
        }),
    [
      crmAttributes,
      currentCrmField,
      normalizedExpectedCrmAttributeTypes,
      usedCrmFields,
    ],
  );

  const handleCrmMappingChange = useCallback(
    (value) => {
      if (typeof onCrmFieldChange === 'function') {
        onCrmFieldChange(component.name, value || '', screenIndex);
      }
    },
    [component?.name, onCrmFieldChange, screenIndex],
  );

  const renderCrmFieldSelector = () => {
    if (!allowCrmSync) return null;

    const groupedAttributes = availableCrmAttributes.reduce((map, attr) => {
      const scopeKey = attr.scope || 'Other';
      if (!map.has(scopeKey)) {
        map.set(scopeKey, []);
      }
      map.get(scopeKey).push(attr);
      return map;
    }, new Map());

    return (
      <div className="space-y-1">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <span>
            CRM field <span className="text-xs text-gray-400">(optional)</span>
          </span>
          <Tooltip title="Map this response to a CRM attribute.">
            <InfoCircleOutlined className="text-gray-400" />
          </Tooltip>
        </label>
        <Select
          value={currentCrmField || undefined}
          allowClear
          placeholder="Select CRM field to sync (optional)"
          className="w-full text-sm"
          style={{ height: '38px', fontSize: '14px' }}
          classNames={{
            popup: {
              root: 'text-sm',
            },
          }}
          showSearch={false}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={handleCrmMappingChange}
          optionLabelProp="label"
          loading={crmAttributesLoading}
        >
          {Array.from(groupedAttributes.entries()).map(([scope, attrs]) => (
            <OptGroup
              key={scope}
              label={
                <div className="pl-2 py-2 text-[14px] uppercase tracking-wide text-black font-[600]">
                  {scope}
                </div>
              }
            >
              {attrs.map((attr) => (
                <Option key={attr.name} value={attr.name} label={attr.label}>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-800">{attr.label}</span>
                    {/* <span className="text-[11px] text-gray-400">{attr.name}</span> */}
                  </div>
                </Option>
              ))}
            </OptGroup>
          ))}
        </Select>
      </div>
    );
  };

  const triggerImagePicker = useCallback(() => {
    if (!flowId) {
      message.warning('Please save the flow before uploading an image.');
      return;
    }
    imageFileInputRef.current?.click();
  }, [flowId]);

  const validateImageFile = (file) => {
    if (!file) return false;

    const allowedTypes = ['png', 'jpg', 'jpeg'];
    const extension = file.name?.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(extension)) {
      message.error('Only PNG or JPG images are supported.');
      return false;
    }

    const maxSizeInBytes = 300 * 1024; // 300KB
    if (file.size > maxSizeInBytes) {
      message.error('Please choose an image under 300KB.');
      return false;
    }

    return true;
  };

  const getImageSource = (base64Data) => {
    if (!base64Data) return '';
    // If it's already a data URL, return as is
    if (base64Data.startsWith('data:')) return base64Data;
    // Otherwise, assume it's raw base64 and add the prefix
    return `data:image/jpeg;base64,${base64Data}`;
  };

  const performImageUpload = (file) => {
    if (!validateImageFile(file)) return;

    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const dataUrl = event.target.result;
        // Extract just the base64 part (remove the data:image/...;base64, prefix)
        const base64Data = dataUrl.split(',')[1];
        handleChange('src', base64Data);
        message.success('Image processed successfully.');
        resolve();
      };

      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        message.error('Failed to process the image. Please try again.');
        resolve();
      };

      // Read the file as a data URL (base64)
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (event?.target) {
      event.target.value = '';
    }
    if (!file) return;

    await performImageUpload(file);
  };

  const handleImageDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    await performImageUpload(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleRemoveImage = () => {
    handleChange('src', '');
  };

  const DATE_FORMAT = 'YYYY-MM-DD';
  const parseDateValue = (value) => {
    if (!value) return null;
    const parsed = dayjs(value, DATE_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  };

  const minDateValue = parseDateValue(component['min-date']);
  const maxDateValue = parseDateValue(component['max-date']);

  const handleMinDateChange = (date) => {
    if (date && maxDateValue && date.isAfter(maxDateValue, 'day')) {
      message.error('Minimum date cannot be after maximum date');
      return;
    }
    handleChange('min-date', date ? date.format(DATE_FORMAT) : '');
  };

  const handleMaxDateChange = (date) => {
    if (date && minDateValue && date.isBefore(minDateValue, 'day')) {
      message.error('Maximum date cannot be before minimum date');
      return;
    }
    handleChange('max-date', date ? date.format(DATE_FORMAT) : '');
  };

  const disableMinDate = (current) => {
    if (!current || !maxDateValue) return false;
    return current.isAfter(maxDateValue, 'day');
  };

  const disableMaxDate = (current) => {
    if (!current || !minDateValue) return false;
    return current.isBefore(minDateValue, 'day');
  };

  useEffect(() => {
    if (isFormChild && component && component['init-value'] !== undefined) {
      if (typeof onUpdate === 'function') {
        onUpdate({ ['init-value']: undefined });
      }
    }
  }, [component, isFormChild, onUpdate]);

  const matchComponentType = (value) => {
    switch (value) {
      case 'TextHeading':
        return 'Text Heading';
      case 'TextSubheading':
        return 'Text Subheading';
      case 'TextCaption':
        return 'Text Caption';
      case 'TextBody':
        return 'Text Body';
      case 'TextInput':
        return 'Text Input';
      case 'TextArea':
        return 'Text Area';
      case 'DatePicker':
        return 'Date Picker';
      case 'CheckboxGroup':
        return 'Checkbox Group';
      case 'RadioButtonsGroup':
        return 'Radio Buttons Group';
      case 'Form':
        return 'Form';
      case 'Dropdown':
        return 'Dropdown';
      case 'OptIn':
        return 'Opt In';
      case 'Footer':
        return 'Footer';
      case 'Image':
        return 'Image';
      default:
        return 'text';
    }
  };

  if (isForm) {
    const formChildren = Array.isArray(component.children)
      ? component.children
      : [];
    const formDroppableId =
      typeof createFormDroppableId === 'function'
        ? createFormDroppableId(screenId, componentIndex)
        : '';

    return (
      <div className="space-y-4">
        <StrictModeDroppable
          droppableId={formDroppableId}
          type="FORM_COMPONENT"
        >
          {(providedForm) => (
            <div
              ref={providedForm.innerRef}
              {...providedForm.droppableProps}
              className="space-y-3"
            >
              {formChildren.length === 0 && (
                <div className="rounded-lg border border-dashed border-gray-200 bg-blue-50 px-4 py-6 text-center text-xs text-gray-500">
                  No content blocks yet. Use "Add content block" below to insert
                  one.
                </div>
              )}

              {formChildren.map((child, childIndex) => {
                if (!child || typeof child !== 'object' || !child.type)
                  return null;

                const draggableId =
                  typeof createFormChildDraggableId === 'function'
                    ? createFormChildDraggableId(
                        screenId,
                        componentIndex,
                        childIndex,
                      )
                    : `${screenId}-${componentIndex}-${childIndex}`;

                return (
                  <Draggable
                    key={draggableId}
                    draggableId={draggableId}
                    index={childIndex}
                    isDragDisabled={child.type === 'Footer'}
                  >
                    {(formProvided) => (
                      <div
                        ref={formProvided.innerRef}
                        {...formProvided.draggableProps}
                      >
                        <FlowComponentEditor
                          component={child}
                          componentIndex={childIndex}
                          screenId={screenId}
                          parentFormIndex={componentIndex}
                          dragHandleProps={formProvided.dragHandleProps}
                          onUpdate={(updates) =>
                            handleUpdateFormComponent?.(
                              screenId,
                              componentIndex,
                              childIndex,
                              updates,
                            )
                          }
                          onDelete={() =>
                            handleDeleteFormComponent?.(
                              screenId,
                              componentIndex,
                              childIndex,
                            )
                          }
                          formUtils={formUtils}
                          optInUtils={optInUtils}
                          isFormChild
                          brandId={brandId}
                          flowId={flowId}
                          crmAttributes={crmAttributes}
                          crmFieldMappings={crmFieldMappings}
                          onCrmFieldChange={onCrmFieldChange}
                          crmAttributesLoading={crmAttributesLoading}
                          screenIndex={screenIndex}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}

              {providedForm.placeholder}
            </div>
          )}
        </StrictModeDroppable>
      </div>
    );
  }

  const containerClassName = [
    'mb-5 overflow-hidden rounded-xl border border-blue-200 shadow-sm transition-all hover:shadow',
    isFormChild ? 'bg-blue-50' : 'bg-white',
  ].join(' ');

  const headerDragHandleProps = isFooter ? {} : dragHandleProps || {};

  let content = null;
  const currentAction = component['on-click-action'] || null;
  const currentActionName = currentAction?.name || 'none';
  const currentActionUrl = currentAction?.url || '';

  content = (
    <>
      {['TextHeading', 'TextSubheading', 'TextCaption', 'TextBody'].includes(
        component.type,
      ) && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Text
            </label>
            <TextArea
              value={component.text}
              onChange={(e) => handleChange('text', e.target.value)}
              placeholder="Enter text"
              maxLength={
                component.type === 'TextHeading'
                  ? 80
                  : component.type === 'TextBody'
                  ? 4096
                  : 500
              }
              rows={component.type === 'TextBody' ? 4 : 2}
              showCount
              style={{ fontSize: '14px', lineHeight: '1.6' }}
            />
            <span className="mt-1 block text-xs text-gray-500">
              Max:{' '}
              {component.type === 'TextHeading'
                ? '80'
                : component.type === 'TextBody'
                ? '4096'
                : '500'}{' '}
              characters
            </span>
          </div>
        </div>
      )}

      {component.type === 'Image' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image
            </label>
            {component.src ? (
              <div className="space-y-3">
                <div className="relative overflow-hidden rounded-lg border border-blue-200 bg-white">
                  <img
                    src={getImageSource(component.src)}
                    alt="Flow component preview"
                    className="w-full rounded-lg object-contain"
                    style={{ maxHeight: 320 }}
                    onError={(e) => {
                      console.error('Error loading image:', component.src);
                      e.target.alt = 'Failed to load image';
                    }}
                  />
                  {isUploadingImage && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm font-medium text-gray-700">
                      Uploading…
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="primary"
                    onClick={triggerImagePicker}
                    loading={isUploadingImage}
                    disabled={isUploadingImage}
                  >
                    {isUploadingImage ? 'Uploading' : 'Replace image'}
                  </Button>
                  <Button
                    onClick={handleRemoveImage}
                    disabled={isUploadingImage}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-blue-300 bg-blue-50 px-4 py-10 text-center transition hover:border-blue-400 hover:bg-blue-100 focus-within:border-blue-400"
                onClick={triggerImagePicker}
                onDragOver={handleDragOver}
                onDrop={handleImageDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    triggerImagePicker();
                  }
                }}
              >
                <PlusOutlined className="text-xl text-[#1daa61]" />
                <p className="mt-2 text-sm font-medium text-gray-700">
                  Drag & drop or click to upload
                </p>
                <p className="text-xs text-gray-500">PNG or JPG up to 300KB</p>
                {isUploadingImage && (
                  <p className="mt-2 text-xs font-medium text-[#1daa61]">
                    Uploading…
                  </p>
                )}
              </div>
            )}
            <input
              ref={imageFileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handleImageUpload}
            />
            <p className="mt-2 text-xs text-gray-500">
              PNG or JPG up to 300KB. Square images work best for WhatsApp flow
              previews.
            </p>
          </div>
        </div>
      )}

      {component.type === 'TextInput' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label
            </label>
            <Input
              value={component.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Enter label"
              maxLength={20}
              showCount
              style={{ height: '38px', fontSize: '14px' }}
            />
            {duplicateWarning}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Input Type
            </label>
            <Select
              value={component['input-type']}
              onChange={(value) => handleChange('input-type', value)}
              className="w-full text-sm"
              dropdownClassName="text-sm"
              style={{ height: '38px', fontSize: '14px' }}
            >
              <Option value="text">Text</Option>
              {/* <Option value="number">Number</Option> */}
              <Option value="email">Email</Option>
              <Option value="password">Password</Option>
              {/* <Option value="passcode">Passcode</Option> */}
              <Option value="phone">Phone</Option>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Characters
              </label>
              <InputNumber
                value={component['min-chars']}
                onChange={(value) => handleChange('min-chars', value)}
                min={0}
                max={component['max-chars'] || 35}
                className="w-full"
                style={{ height: '38px', fontSize: '14px', width: '100%' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Characters
              </label>
              <InputNumber
                value={component['max-chars']}
                onChange={(value) => handleChange('max-chars', value)}
                min={component['min-chars'] || 0}
                max={4096}
                className="w-full"
                style={{ height: '38px', fontSize: '14px', width: '100%' }}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Helper Text
            </label>
            <Input
              value={component['helper-text']}
              onChange={(e) => handleChange('helper-text', e.target.value)}
              placeholder="Optional helper text"
              maxLength={80}
              showCount
              style={{ height: '38px', fontSize: '14px' }}
            />
          </div>
          {currentActionName === 'open_url' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL
              </label>
              <Input
                value={currentActionUrl}
                onChange={(e) =>
                  handleChange('on-click-action', {
                    name: 'open_url',
                    url: e.target.value,
                  })
                }
                placeholder="https://example.com"
                style={{ height: '38px', fontSize: '14px' }}
              />
            </div>
          )}
        </div>
      )}

      {component.type === 'TextArea' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label
            </label>
            <Input
              value={component.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Enter label"
              maxLength={20}
              showCount
              style={{ height: '38px', fontSize: '14px' }}
            />
            {duplicateWarning}
          </div>
          {currentActionName === 'open_url' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL
              </label>
              <Input
                value={currentActionUrl}
                onChange={(e) =>
                  handleChange('on-click-action', {
                    name: 'open_url',
                    url: e.target.value,
                  })
                }
                placeholder="https://example.com"
                style={{ height: '38px', fontSize: '14px' }}
              />
            </div>
          )}
        </div>
      )}

      {component.type === 'DatePicker' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label
            </label>
            <Input
              value={component.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Enter label"
              maxLength={20}
              showCount
              style={{ height: '38px', fontSize: '14px' }}
            />
            {duplicateWarning}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Date
            </label>
            <DatePicker
              value={minDateValue}
              onChange={handleMinDateChange}
              format={DATE_FORMAT}
              allowClear
              className="w-full"
              placeholder="Select minimum date"
              disabledDate={disableMinDate}
              style={{ height: '38px', fontSize: '14px', width: '100%' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maximum Date
            </label>
            <DatePicker
              value={maxDateValue}
              onChange={handleMaxDateChange}
              format={DATE_FORMAT}
              allowClear
              className="w-full"
              placeholder="Select maximum date"
              disabledDate={disableMaxDate}
              style={{ height: '38px', fontSize: '14px', width: '100%' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Helper Text
            </label>
            <Input
              value={component['helper-text']}
              onChange={(e) => handleChange('helper-text', e.target.value)}
              placeholder="Optional helper text"
              maxLength={80}
              showCount
              style={{ height: '38px', fontSize: '14px' }}
            />
          </div>
        </div>
      )}

      {component.type === 'RadioButtonsGroup' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question
            </label>
            <Input
              value={component.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Enter question"
              maxLength={30}
              showCount
              style={{ height: '38px', fontSize: '14px' }}
            />
            {duplicateWarning}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options
            </label>
            <div className="flex flex-col gap-1">
              {componentOptions.map((option, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={option.title}
                    onChange={(e) => {
                      const next = [...componentOptions];
                      next[idx] = updateOptionTitle(
                        next[idx],
                        e.target.value,
                        `Option ${idx + 1}`,
                      );
                      handleChange('data-source', next);
                    }}
                    placeholder={`Option ${idx + 1}`}
                    maxLength={30}
                    showCount
                    style={{ height: '38px', fontSize: '14px' }}
                  />
                  <Button
                    type="text"
                    className="flex items-center justify-center text-red-500 hover:text-red-600 !p-1"
                    icon={<CustomDelete color="currentColor" size={26} />}
                    onClick={() => {
                      const next = componentOptions.filter((_, i) => i !== idx);
                      if (next.length > 0) {
                        handleChange('data-source', next);
                      } else {
                        message.error('At least one option is required');
                      }
                    }}
                    disabled={componentOptions.length <= 1}
                  />
                </div>
              ))}
            </div>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                if (componentOptions.length >= 20) {
                  message.error('Maximum 20 options allowed');
                  return;
                }
                const next = [
                  ...componentOptions,
                  createOptionEntry(`Option ${componentOptions.length + 1}`),
                ];
                handleChange('data-source', next);
              }}
              className="w-full mt-2"
            >
              Add option
            </Button>
          </div>
        </div>
      )}

      {component.type === 'CheckboxGroup' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question
            </label>
            <Input
              value={component.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Enter question"
              maxLength={30}
              showCount
              style={{ height: '38px', fontSize: '14px' }}
            />
            {duplicateWarning}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options
            </label>
            {componentOptions.map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  value={option.title}
                  onChange={(e) => {
                    const next = [...componentOptions];
                    next[idx] = updateOptionTitle(
                      next[idx],
                      e.target.value,
                      `Option ${idx + 1}`,
                    );
                    handleChange('data-source', next);
                  }}
                  placeholder={`Option ${idx + 1}`}
                  maxLength={30}
                  className="mb-1"
                  showCount
                  style={{ height: '38px', fontSize: '14px' }}
                />
                <Button
                  type="text"
                  className="flex items-center justify-center text-red-500 hover:text-red-600 !p-1"
                  icon={<CustomDelete color="currentColor" size={26} />}
                  onClick={() => {
                    const next = componentOptions.filter((_, i) => i !== idx);
                    if (next.length > 0) {
                      handleChange('data-source', next);
                    } else {
                      message.error('At least one option is required');
                    }
                  }}
                  disabled={componentOptions.length <= 1}
                />
              </div>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                if (componentOptions.length >= 20) {
                  message.error('Maximum 20 options allowed');
                  return;
                }
                const next = [
                  ...componentOptions,
                  createOptionEntry(`Option ${componentOptions.length + 1}`),
                ];
                handleChange('data-source', next);
              }}
              className="w-full"
            >
              Add option
            </Button>
          </div>
        </div>
      )}

      {component.type === 'Dropdown' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label
            </label>
            <Input
              value={component.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Enter label"
              maxLength={30}
              showCount
              style={{ height: '38px', fontSize: '14px' }}
            />
            {duplicateWarning}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options
            </label>
            {componentOptions.map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  value={option.title}
                  onChange={(e) => {
                    const next = [...componentOptions];
                    next[idx] = updateOptionTitle(
                      next[idx],
                      e.target.value,
                      `Option ${idx + 1}`,
                    );
                    handleChange('data-source', next);
                  }}
                  placeholder={`Option ${idx + 1}`}
                  maxLength={30}
                  className="mb-1"
                  showCount
                  style={{ height: '38px', fontSize: '14px' }}
                />
                <Button
                  type="text"
                  className="flex items-center justify-center text-red-500 hover:text-red-600 !p-1"
                  icon={<CustomDelete color="currentColor" size={26} />}
                  onClick={() => {
                    const next = componentOptions.filter((_, i) => i !== idx);
                    if (next.length > 0) {
                      handleChange('data-source', next);
                    } else {
                      message.error('At least one option is required');
                    }
                  }}
                  disabled={componentOptions.length <= 1}
                />
              </div>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                if (componentOptions.length >= 100) {
                  message.error('Maximum 100 options allowed');
                  return;
                }
                const next = [
                  ...componentOptions,
                  createOptionEntry(`Option ${componentOptions.length + 1}`),
                ];
                handleChange('data-source', next);
              }}
              className="w-full"
            >
              Add option
            </Button>
          </div>
        </div>
      )}

      {component.type === 'OptIn' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 pt-[12px]">
              Label
            </label>
            <TextArea
              value={component.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="I agree to the terms."
              maxLength={280}
              showCount
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ fontSize: '14px', lineHeight: '1.6' }}
            />
            {duplicateWarning}
          </div>
          {!isFormChild ? (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Default to selected
              </span>
              <Switch
                checked={Boolean(component['init-value'])}
                onChange={(checked) =>
                  handleChange('init-value', checked ? true : undefined)
                }
              />
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              Default state is managed by the enclosing form.
            </p>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              Read more link{' '}
              <span className="text-xs text-gray-400">· Optional</span>
            </p>
            {(() => {
              const readMoreScreenId =
                currentActionName === 'navigate'
                  ? currentAction?.next?.name
                  : null;
              const readMoreScreen =
                readMoreScreenId &&
                typeof optInUtils?.getReadMoreScreen === 'function'
                  ? optInUtils.getReadMoreScreen(readMoreScreenId)
                  : null;

              if (readMoreScreen) {
                return (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          "Read more" screen
                        </p>
                        <p className="text-xs text-gray-500">
                          {readMoreScreen.title || readMoreScreen.id}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                          onClick={() =>
                            optInUtils?.editReadMoreScreen?.(readMoreScreenId)
                          }
                        >
                          Edit content
                        </button>
                        <button
                          type="button"
                          className="text-sm font-semibold text-red-500 hover:text-red-600"
                          onClick={() =>
                            optInUtils?.removeReadMoreScreen?.({
                              readMoreScreenId,
                              parentScreenId: screenId,
                            })
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="space-y-3">
                  <button
                    type="button"
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:border-blue-300 hover:bg-white"
                    onClick={() =>
                      optInUtils?.addReadMoreScreen?.({
                        screenId,
                        componentIndex,
                        formIndex: parentFormIndex,
                      })
                    }
                    disabled={
                      typeof optInUtils?.addReadMoreScreen !== 'function'
                    }
                  >
                    <PlusOutlined className="text-xs" />
                    Add "Read more" screen
                  </button>

                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Alternate link action
                    </label>
                    <Select
                      value={
                        currentActionName === 'open_url' ? 'open_url' : 'none'
                      }
                      className="w-full"
                      style={{ height: '38px', fontSize: '14px' }}
                      onChange={(value) => {
                        if (value === 'open_url') {
                          handleChange('on-click-action', {
                            name: 'open_url',
                            url: currentActionUrl || 'https://',
                          });
                        } else {
                          handleChange('on-click-action', null);
                        }
                      }}
                      options={[
                        { value: 'none', label: 'No link' },
                        { value: 'open_url', label: 'Open URL' },
                      ]}
                    />
                  </div> */}
                  {currentActionName === 'open_url' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        URL
                      </label>
                      <Input
                        value={currentActionUrl}
                        onChange={(e) =>
                          handleChange('on-click-action', {
                            name: 'open_url',
                            url: e.target.value,
                          })
                        }
                        placeholder="https://example.com"
                        style={{ height: '38px', fontSize: '14px' }}
                      />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {isInteractiveField && renderCrmFieldSelector()}

      {component.type === 'Footer' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Button Text
            </label>
            <Input
              value={component.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Enter button text"
              maxLength={20}
              showCount
              style={{ height: '38px', fontSize: '14px' }}
            />
          </div>
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-gray-700">
            Footer automatically navigates to the next screen based on screen
            order.
          </div>
        </div>
      )}
    </>
  );

  const contentWrapperClassName = ['space-y-4', 'bg-white', 'px-4', 'py-4'];
  if (isFormChild) {
    contentWrapperClassName[1] = 'bg-blue-50';
  }

  return (
    <div className={containerClassName}>
      <div className="flex items-center justify-between border-b border-blue-200 bg-blue-100 px-4 py-3">
        <div className="flex items-center gap-3">
          {isFooter ? null : (
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full border border-blue-200 bg-white text-lg text-gray-600"
              {...headerDragHandleProps}
            >
              <HolderOutlined />
            </span>
          )}
          <span className="text-sm font-semibold text-gray-700">
            {matchComponentType(component.type)}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {hasRequiredToggle && (
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <span>Required</span>
              <Switch
                size="small"
                checked={component.required}
                onChange={(checked) => handleChange('required', checked)}
              />
            </div>
          )}
          {!isFooter && (
            <Button
              type="text"
              className="flex items-center justify-center text-red-500 hover:text-red-600 !p-1.5"
              icon={<CustomDelete color="#ef4444" size={30} />}
              onClick={deleteComponent}
            />
          )}
        </div>
      </div>

      <div className={contentWrapperClassName.join(' ')}>{content}</div>
    </div>
  );
}
